import { createSlice } from "@reduxjs/toolkit"
import { withFirestoreHydration } from "../middleware/firestorePersistence"

// Initial state for expenses
const initialState = {
  expenses: {},
  recurringExpenses: {},
  expenseCategories: {},
  tags: {},
  searchHistory: [],
  filters: {
    dateRange: {
      start: null,
      end: null,
    },
    categories: [],
    tags: [],
    amountRange: {
      min: null,
      max: null,
    },
    merchants: [],
    searchTerm: "",
  },
  analytics: {
    monthlyTrends: {},
    categoryBreakdowns: {},
    spendingPatterns: {},
    predictions: {},
  },
  isLoading: false,
  error: null,
  _hydrated: false,
}

const expensesSlice = createSlice({
  name: "expenses",
  initialState,
  reducers: {
    // Expense management
    setExpenses: (state, action) => {
      const { month, expenses } = action.payload
      state.expenses[month] = expenses
      state.isLoading = false
    },

    addExpense: (state, action) => {
      const { month, expense } = action.payload
      if (!state.expenses[month]) {
        state.expenses[month] = {}
      }
      state.expenses[month][expense.id] = {
        ...expense,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    },

    updateExpense: (state, action) => {
      const { month, expenseId, updates } = action.payload
      if (state.expenses[month] && state.expenses[month][expenseId]) {
        state.expenses[month][expenseId] = {
          ...state.expenses[month][expenseId],
          ...updates,
          updatedAt: new Date().toISOString(),
        }
      }
    },

    deleteExpense: (state, action) => {
      const { month, expenseId } = action.payload
      if (state.expenses[month]) {
        delete state.expenses[month][expenseId]
      }
    },

    bulkUpdateExpenses: (state, action) => {
      const { month, updates } = action.payload
      if (state.expenses[month]) {
        Object.keys(updates).forEach((expenseId) => {
          if (state.expenses[month][expenseId]) {
            state.expenses[month][expenseId] = {
              ...state.expenses[month][expenseId],
              ...updates[expenseId],
              updatedAt: new Date().toISOString(),
            }
          }
        })
      }
    },

    // Recurring expenses
    setRecurringExpenses: (state, action) => {
      state.recurringExpenses = action.payload
    },

    addRecurringExpense: (state, action) => {
      const expense = action.payload
      state.recurringExpenses[expense.id] = {
        ...expense,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    },

    updateRecurringExpense: (state, action) => {
      const { expenseId, updates } = action.payload
      if (state.recurringExpenses[expenseId]) {
        state.recurringExpenses[expenseId] = {
          ...state.recurringExpenses[expenseId],
          ...updates,
          updatedAt: new Date().toISOString(),
        }
      }
    },

    deleteRecurringExpense: (state, action) => {
      const expenseId = action.payload
      delete state.recurringExpenses[expenseId]
    },

    // Category management
    setExpenseCategories: (state, action) => {
      state.expenseCategories = action.payload
    },

    addExpenseCategory: (state, action) => {
      const category = action.payload
      state.expenseCategories[category.id] = category
    },

    updateExpenseCategory: (state, action) => {
      const { categoryId, updates } = action.payload
      if (state.expenseCategories[categoryId]) {
        state.expenseCategories[categoryId] = {
          ...state.expenseCategories[categoryId],
          ...updates,
        }
      }
    },

    deleteExpenseCategory: (state, action) => {
      const categoryId = action.payload
      delete state.expenseCategories[categoryId]
    },

    // Tags management
    setTags: (state, action) => {
      state.tags = action.payload
    },

    addTag: (state, action) => {
      const tag = action.payload
      state.tags[tag.id] = tag
    },

    updateTag: (state, action) => {
      const { tagId, updates } = action.payload
      if (state.tags[tagId]) {
        state.tags[tagId] = {
          ...state.tags[tagId],
          ...updates,
        }
      }
    },

    deleteTag: (state, action) => {
      const tagId = action.payload
      delete state.tags[tagId]
    },

    // Filters
    updateFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      }
    },

    setDateRangeFilter: (state, action) => {
      const { start, end } = action.payload
      state.filters.dateRange = { start, end }
    },

    setCategoryFilter: (state, action) => {
      state.filters.categories = action.payload
    },

    setTagFilter: (state, action) => {
      state.filters.tags = action.payload
    },

    setAmountRangeFilter: (state, action) => {
      const { min, max } = action.payload
      state.filters.amountRange = { min, max }
    },

    setMerchantFilter: (state, action) => {
      state.filters.merchants = action.payload
    },

    setSearchTerm: (state, action) => {
      state.filters.searchTerm = action.payload
    },

    clearFilters: (state) => {
      state.filters = initialState.filters
    },

    // Search history
    addToSearchHistory: (state, action) => {
      const searchTerm = action.payload
      // Add to beginning of array and limit to 10 items
      state.searchHistory = [
        searchTerm,
        ...state.searchHistory.filter((term) => term !== searchTerm),
      ].slice(0, 10)
    },

    clearSearchHistory: (state) => {
      state.searchHistory = []
    },

    // Analytics
    setAnalytics: (state, action) => {
      state.analytics = {
        ...state.analytics,
        ...action.payload,
      }
    },

    setMonthlyTrends: (state, action) => {
      state.analytics.monthlyTrends = action.payload
    },

    setCategoryBreakdowns: (state, action) => {
      state.analytics.categoryBreakdowns = action.payload
    },

    setSpendingPatterns: (state, action) => {
      state.analytics.spendingPatterns = action.payload
    },

    setPredictions: (state, action) => {
      state.analytics.predictions = action.payload
    },

    // UI state
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },

    setError: (state, action) => {
      state.error = action.payload
      state.isLoading = false
    },

    clearError: (state) => {
      state.error = null
    },

    // Firestore hydration (handled by middleware)
    hydrateFromFirestore: (state, action) => {
      return {
        ...state,
        ...action.payload,
        _hydrated: true,
      }
    },
  },
})

// Export actions
export const {
  setExpenses,
  addExpense,
  updateExpense,
  deleteExpense,
  bulkUpdateExpenses,
  setRecurringExpenses,
  addRecurringExpense,
  updateRecurringExpense,
  deleteRecurringExpense,
  setExpenseCategories,
  addExpenseCategory,
  updateExpenseCategory,
  deleteExpenseCategory,
  setTags,
  addTag,
  updateTag,
  deleteTag,
  updateFilters,
  setDateRangeFilter,
  setCategoryFilter,
  setTagFilter,
  setAmountRangeFilter,
  setMerchantFilter,
  setSearchTerm,
  clearFilters,
  addToSearchHistory,
  clearSearchHistory,
  setAnalytics,
  setMonthlyTrends,
  setCategoryBreakdowns,
  setSpendingPatterns,
  setPredictions,
  setLoading,
  setError,
  clearError,
  hydrateFromFirestore,
} = expensesSlice.actions

// Selectors
export const selectExpenses = (state) => state.expenses.expenses
export const selectExpensesForMonth = (state, month) =>
  state.expenses.expenses[month] || {}
export const selectRecurringExpenses = (state) =>
  state.expenses.recurringExpenses
export const selectExpenseCategories = (state) =>
  state.expenses.expenseCategories
export const selectTags = (state) => state.expenses.tags
export const selectFilters = (state) => state.expenses.filters
export const selectSearchHistory = (state) => state.expenses.searchHistory
export const selectAnalytics = (state) => state.expenses.analytics
export const selectExpensesLoading = (state) => state.expenses.isLoading
export const selectExpensesError = (state) => state.expenses.error
export const selectExpensesHydrated = (state) => state.expenses._hydrated

// Composite selectors
export const selectFilteredExpenses = (state, month) => {
  const expenses = state.expenses.expenses[month] || {}
  const filters = state.expenses.filters

  let filteredExpenses = Object.values(expenses)

  // Apply filters
  if (filters.categories.length > 0) {
    filteredExpenses = filteredExpenses.filter((expense) =>
      filters.categories.includes(expense.categoryId)
    )
  }

  if (filters.tags.length > 0) {
    filteredExpenses = filteredExpenses.filter(
      (expense) =>
        expense.tags && expense.tags.some((tag) => filters.tags.includes(tag))
    )
  }

  if (filters.amountRange.min !== null) {
    filteredExpenses = filteredExpenses.filter(
      (expense) => expense.amount >= filters.amountRange.min
    )
  }

  if (filters.amountRange.max !== null) {
    filteredExpenses = filteredExpenses.filter(
      (expense) => expense.amount <= filters.amountRange.max
    )
  }

  if (filters.merchants.length > 0) {
    filteredExpenses = filteredExpenses.filter((expense) =>
      filters.merchants.includes(expense.merchant)
    )
  }

  if (filters.searchTerm) {
    const searchTerm = filters.searchTerm.toLowerCase()
    filteredExpenses = filteredExpenses.filter(
      (expense) =>
        expense.description.toLowerCase().includes(searchTerm) ||
        expense.merchant.toLowerCase().includes(searchTerm)
    )
  }

  return filteredExpenses
}

export const selectExpensesByCategory = (state, month) => {
  const expenses = state.expenses.expenses[month] || {}
  const categories = state.expenses.expenseCategories

  const grouped = {}
  Object.values(expenses).forEach((expense) => {
    const categoryId = expense.categoryId || "uncategorized"
    if (!grouped[categoryId]) {
      grouped[categoryId] = {
        category: categories[categoryId] || { name: "Uncategorized" },
        expenses: [],
        total: 0,
      }
    }
    grouped[categoryId].expenses.push(expense)
    grouped[categoryId].total += expense.amount
  })

  return grouped
}

// Enhanced reducer with Firestore hydration
export default withFirestoreHydration(expensesSlice.reducer)
